/** @typedef {import('./config_schema.js').PortalConfig} PortalConfig */

export const DEFAULT_BRANDING = {
  enabled: false,
  primaryColor: '#2563eb',
  backgroundColor: '#ffffff',
  cardColor: '#e6e6e6',
  textColor: '#334155',
  description: '',
  logoUrl: '',
  companyName: ''
};

/**
 * @param {PortalConfig} config
 */
export function isBrandingEnabled(config) {
  const b = config.html?.sections?.branding;
  if (typeof b?.enabled === 'boolean') return b.enabled;
  return Boolean(
    sanitizeLogoUrl(b?.logoUrl) ||
      String(b?.companyName || '').trim() ||
      String(b?.description || '').trim()
  );
}

/**
 * @param {string} value
 * @param {string} fallback
 */
export function sanitizeColor(value, fallback) {
  const s = String(value || '').trim();
  if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(s)) return s;
  return fallback;
}

/**
 * @param {string} url
 * @returns {string}
 */
export function sanitizeLogoUrl(url) {
  const s = String(url || '').trim();
  if (!s) return '';
  try {
    const u = new URL(s);
    if (u.protocol === 'https:' || u.protocol === 'http:') return s;
  } catch {
    /* invalid */
  }
  return '';
}

/**
 * @param {PortalConfig} config
 */
export function resolveBranding(config) {
  const b = config.html?.sections?.branding || {};
  return {
    primaryColor: sanitizeColor(b.primaryColor, DEFAULT_BRANDING.primaryColor),
    backgroundColor: sanitizeColor(b.backgroundColor, DEFAULT_BRANDING.backgroundColor),
    cardColor: sanitizeColor(b.cardColor, DEFAULT_BRANDING.cardColor),
    textColor: sanitizeColor(b.textColor, DEFAULT_BRANDING.textColor),
    description: String(b.description || '').trim(),
    logoUrl: sanitizeLogoUrl(b.logoUrl),
    companyName: String(b.companyName || '').trim()
  };
}

/**
 * @param {ReturnType<typeof resolveBranding>} branding
 */
export function emitBrandingCssVars(branding) {
  return `:root {
        --pb-primary: ${branding.primaryColor};
        --pb-bg: ${branding.backgroundColor};
        --pb-card: ${branding.cardColor};
        --pb-text: ${branding.textColor};
      }`;
}

export const PORTAL_BRAND_CSS = `
      .portal-brand {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 16px;
        margin-bottom: 20px;
      }
      .portal-brand__logo {
        max-width: 140px;
        max-height: 56px;
        width: auto;
        height: auto;
        object-fit: contain;
      }
      .portal-brand__name {
        font-size: 14px;
        font-weight: 700;
        color: var(--pb-text, #64748b);
        letter-spacing: 0.04em;
      }
      .portal-brand__headings { text-align: right; line-height: 1.3; }
      .portal-brand__title {
        font-size: 16px;
        font-weight: 700;
        letter-spacing: 0.06em;
        color: var(--pb-text, #64748b);
      }
      .portal-brand__subtitle {
        font-size: 12px;
        letter-spacing: 0.06em;
        color: #94a3b8;
      }
      .portal-brand__desc {
        margin: 0 0 16px;
        color: #64748b;
        font-size: 14px;
        line-height: 1.5;
      }
`;

/**
 * @param {string[]} lines
 * @param {ReturnType<typeof resolveBranding>} branding
 * @param {{ heading?: string, subheading?: string, escHtml: (s: string) => string, wrapperClass?: string, skipDescription?: boolean }} opts
 */
export function emitBrandingHeader(lines, branding, opts) {
  const { heading = '', subheading = '', escHtml, wrapperClass = 'portal-brand', skipDescription = false } =
    opts;
  const hasHeader =
    branding.logoUrl ||
    branding.companyName ||
    heading ||
    subheading ||
    (!skipDescription && branding.description);
  if (!hasHeader) return;

  if (branding.logoUrl || branding.companyName || heading || subheading) {
    lines.push(`    <div class="${wrapperClass}">`);
    if (branding.logoUrl) {
      const alt = escHtml(branding.companyName || 'Company logo');
      lines.push(
        `      <img class="portal-brand__logo logo" src="${escHtml(branding.logoUrl)}" alt="${alt}">`
      );
    } else if (branding.companyName) {
      lines.push(`      <div class="portal-brand__name header-brand">${escHtml(branding.companyName)}</div>`);
    }
    if (heading || subheading) {
      lines.push('      <div class="portal-brand__headings header-text">');
      if (heading) {
        lines.push(`        <div class="portal-brand__title header-title">${escHtml(heading)}</div>`);
      }
      if (subheading) {
        lines.push(
          `        <div class="portal-brand__subtitle header-subtitle">${escHtml(subheading)}</div>`
        );
      }
      lines.push('      </div>');
    }
    lines.push('    </div>');
  }

  if (!skipDescription && branding.description) {
    emitBrandingDescription(lines, branding, escHtml);
  }
}

/**
 * @param {string[]} lines
 * @param {ReturnType<typeof resolveBranding>} branding
 * @param {(s: string) => string} escHtml
 * @param {string} [wrapperClass]
 */
export function emitBrandingDescription(lines, branding, escHtml, wrapperClass = 'portal-brand__intro card-top full-width') {
  if (!branding.description) return;
  lines.push(`    <div class="${wrapperClass}">`);
  lines.push(`      <p class="muted portal-brand__desc">${escHtml(branding.description)}</p>`);
  lines.push('    </div>');
}
